/**
 * BlightSkillEffect — Poison tree Apotheosis: territory-claiming toxic zone.
 *
 * Blight is the Corrode-path apotheosis. It synthesises everything on the
 * Haze branch (slow + armor debuff) and Acid Rain branch (sustained DoT)
 * into a single, massive zone at peak potency — 4-tile radius, 25 seconds,
 * heavy DoT, instant armor corrosion on entry, and a 20% movement penalty.
 *
 * Zone geometry: circle, 4 tiles radius, centered on the targeted position.
 *
 * On enemy entry: applies a single `corrosion` stack at `blightArmorReduction`
 * intensity (default 0.15 = 15% flat armor reduction), permanent (durationMs -1).
 * Tracked via `_corrosionApplied` Set keyed on `${zoneId}:${entityId}:${entryTime}`
 * so re-entry fires again and the same entry does not double-stack.
 *
 * Dead Air (haze_passive): suppressesSound queried at activation time, same
 * as HazeSkillEffect and MiasmaSkillEffect.
 *
 * Intensity inherits Potent Compound (scope: 'taint') and Toxicologist
 * (tree-wide dotDamageMultiplier), identical to Haze.
 *
 * Cooldown tier: very_long (4000ms).
 *
 * Pure logic — no Phaser deps.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see poison-spec.md § Blight (blight)
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { ZoneConfig, ActiveZone } from '../../../systems/ZoneSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface BlightDeps {
  readonly zoneSystem: {
    create(config: ZoneConfig): string;
    getZone(id: string): ActiveZone | undefined;
    getEntityDwellTimeMs(zoneId: string, entityId: string): number;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getTreePassiveEffect(
      treeName: string,
      key: string,
      aggregation: 'sum' | 'product' | 'max' | 'min',
    ): number;
  };
  readonly targeting: {
    /** World-space aim position where Blight is planted. */
    getTargetPosition(): { x: number; y: number };
  };
  /** Required for armor corrosion on zone entry. */
  readonly applyEntityStatus?: (
    entityId: string,
    defId: string,
    opts: { sourceSkillId: string; durationMs: number; intensity: number },
  ) => void;
  /** Required to avoid double-applying corrosion beyond one stack per entry. */
  readonly getEntityEffectStacks?: (entityId: string, defId: string) => number;
  /** Required for Choking: apply post-exposure slow when entity exits after ≥ threshold. */
  readonly applySlowEffect?: (
    entityId: string,
    opts: { sourceSkillId: string; durationMs: number; intensity: number },
  ) => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface BlightConfig {
  readonly cooldownMs: number;       // very_long tier = 4000ms
  readonly baseRadiusTiles: number;  // 4 tiles
  readonly baseDurationMs: number;   // 25000ms
  readonly baseIntensity: number;    // 8 per tick (heavy DoT)
  readonly speedMultiplier: number;  // 0.8 (20% slow)
  readonly chokingThresholdMs: number; // 3000ms — minimum exposure for post-exit slow
}

const DEFAULT_CONFIG: BlightConfig = {
  cooldownMs: 4000,
  baseRadiusTiles: 4,
  baseDurationMs: 25000,
  baseIntensity: 8,
  speedMultiplier: 0.8,
  chokingThresholdMs: 3000,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class BlightSkillEffect implements SkillEffect {
  readonly skillId = 'blight';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: BlightDeps;
  private readonly config: BlightConfig;

  private _cooldownRemaining = 0;
  private _activationsCount = 0;
  private _zoneIds: string[] = [];

  /**
   * Tracks which (zoneId, entityId, entryTime) triples have already had
   * corrosion applied for this entry instance. Key: `${zoneId}:${entityId}:${entryTime}`.
   * Using entryTime in the key ensures re-entry (new timestamp) fires again.
   */
  private readonly _corrosionApplied = new Set<string>();

  constructor(deps: BlightDeps, config: Partial<BlightConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activationsCount(): number { return this._activationsCount; }
  get zoneIds(): readonly string[] { return this._zoneIds; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    const targetPos = this.deps.targeting.getTargetPosition();
    const intensity = this._resolveIntensity();

    // Dead Air passive: suppress sound detection when unlocked
    const suppressesSound =
      this.deps.resolver.getTreePassiveEffect('poison', 'suppressesSound', 'max') > 0;

    // Choking: post-exposure slow on exit if entity was inside for ≥ threshold
    const slowAmount = this.deps.resolver.getAdditiveEffect('blight', 'postExposureSlowAmount');
    const onEntityExit =
      slowAmount > 0 && this.deps.applySlowEffect
        ? this._makeChokingExitCallback(slowAmount)
        : undefined;

    const zoneId = `blight_zone_${this._activationsCount}`;

    this.deps.zoneSystem.create({
      id: zoneId,
      type: 'blight',
      position: { x: targetPos.x, y: targetPos.y },
      durationMs: this.config.baseDurationMs,
      sourceSkillId: 'blight',
      shape: 'circle',
      radiusTiles: this.config.baseRadiusTiles,
      dotDefId: 'poison',
      dotIntensity: intensity,
      tickIntervalMs: 1000,
      speedMultiplier: this.config.speedMultiplier,
      suppressesSound: suppressesSound || undefined,
      onEntityExit,
    });

    this._zoneIds.push(zoneId);
    this._cooldownRemaining = this.config.cooldownMs;
    this._activationsCount++;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
    this._checkBlightEntry();
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Corrosion on entry: for each active blight zone, iterate entityEntryTimes.
   * Any entity present (entryTime recorded) that has not yet received a corrosion
   * stack for this entry instance gets one applied immediately.
   *
   * Key format: `${zoneId}:${entityId}:${entryTime}` — re-entry (new timestamp)
   * produces a new key and fires again. Exits do not re-apply.
   *
   * No-ops if applyEntityStatus is not injected or blightArmorReduction is 0.
   */
  private _checkBlightEntry(): void {
    if (!this.deps.applyEntityStatus) return;

    const armorReduction = this.deps.resolver.getAdditiveEffect(
      'blight', 'blightArmorReduction',
    );
    if (armorReduction <= 0) return;

    for (const zoneId of this._zoneIds) {
      const zone = this.deps.zoneSystem.getZone(zoneId);
      if (!zone) continue;

      for (const [entityId, entryTime] of zone.entityEntryTimes) {
        const key = `${zoneId}:${entityId}:${entryTime}`;
        if (!this._corrosionApplied.has(key)) {
          this.deps.applyEntityStatus(entityId, 'corrosion', {
            sourceSkillId: 'blight',
            durationMs: -1,
            intensity: armorReduction,
          });
          this._corrosionApplied.add(key);
        }
      }
    }
  }

  /**
   * Choking: build the onEntityExit callback embedded in each blight zone config.
   * Captures slowAmount and resolves slowDurationMs at creation time so the
   * callback is a pure closure over stable values.
   */
  private _makeChokingExitCallback(
    slowAmount: number,
  ): (entityId: string, _zone: ActiveZone, dwellMs: number, _exitX: number, _exitY: number) => void {
    const slowDurationMs = this.deps.resolver.getAdditiveEffect('blight', 'postExposureSlowDurationMs');
    const thresholdMs = this.config.chokingThresholdMs;
    const applySlowEffect = this.deps.applySlowEffect!;

    return (entityId: string, _zone, dwellMs: number) => {
      if (dwellMs >= thresholdMs) {
        applySlowEffect(entityId, {
          sourceSkillId: 'blight',
          durationMs: slowDurationMs,
          intensity: slowAmount,
        });
      }
    };
  }

  /**
   * Resolve final DoT intensity, inheriting Potent Compound (+dotDamagePerTick
   * from scope 'taint') and Toxicologist (product multiplier, tree-wide).
   */
  private _resolveIntensity(): number {
    const base = this.config.baseIntensity;
    const bonus = this.deps.resolver.getAdditiveEffect('taint', 'dotDamagePerTick');
    const multiplier = this.deps.resolver.getTreePassiveEffect(
      'poison', 'dotDamageMultiplier', 'product',
    );
    return (base + bonus) * multiplier;
  }
}
